import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Col, Row, Media } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../../Common/DataTable';
import { Edit, X, UserPlus, UserX } from 'react-feather';
import { Btn } from '../../../../AbstractElements';
import { toast } from 'react-toastify';
import { fetchTableData, assignFinalCompensationToSelf, releaseFinalCompensationFromSelf } from '../../../../Api/ApiMethods';
import FileCloseModal from './FileCloseModal';
import FilesCollapsible from '../../../Common/FilesCollapsible';

const CompensationInformations = ({ arbitrationId, arbitrationData, refreshArbitrationData }) => {
    const navigate = useNavigate();
    const [, setSelectedRows] = useState([]);
    const [clearSelectedRows] = useState(false);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [isAssigning, setIsAssigning] = useState(false);
    const [isReleasing, setIsReleasing] = useState(false);
    const [isFileCloseModalOpen, setIsFileCloseModalOpen] = useState(false);

    const finalCompensationApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/FinalCompensation';

    const logSetData = (newData) => {
        setData(newData);
    };

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    const loadData = useCallback(() => {
        if (arbitrationId) {
            const modifiedApiEndpoint = `${finalCompensationApiUrl}?arbitrationId=${arbitrationId}`;
            fetchTableData({
                apiEndpoint: modifiedApiEndpoint,
                page,
                pageSize,
                search: '',
                setDataCallback: logSetData,
                setTotalRowsCallback: setTotalRows,
            });
        }
    }, [arbitrationId, finalCompensationApiUrl, page, pageSize]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const refreshData = () => {
        loadData();
        if (refreshArbitrationData) refreshArbitrationData();
    };

    const handleFileClose = () => {
        setIsFileCloseModalOpen(true);
    };

    // Dosya kapama butonu görünürlüğü — RulingInformations pattern'i ile aynı
    const shouldShowFileCloseButton = () => {
        const currentState = arbitrationData?.arbitrationState?.title;
        return !['Tekrar Başvuru Bekleniyor', 'Kapama Bekleniyor'].includes(currentState);
    };

    const handleAssignToSelf = async (compensationId) => {
        if (isAssigning) return;
        setIsAssigning(true);
        try {
            await assignFinalCompensationToSelf({ finalCompensationId: compensationId });
            toast.success('Kapak kendinize atandı.');
            refreshData();
        } catch { }
        finally { setIsAssigning(false); }
    };

    const handleReleaseFromSelf = async (compensationId) => {
        if (isReleasing) return;
        setIsReleasing(true);
        try {
            await releaseFinalCompensationFromSelf({ finalCompensationId: compensationId });
            toast.success('Kapak görevi kaldırıldı.');
            refreshData();
        } catch { }
        finally { setIsReleasing(false); }
    };

    const formatCurrency = (value) => {
        if (value == null) return '-';
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
    };

    const formatDate = (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString('tr-TR');
    };

    const columns = [
        { name: 'Plaka', selector: row => row.licensePlate || '-', sortable: true, minWidth: '100px' },
        { name: 'Kaza Tarihi', selector: row => formatDate(row.accidentDate), sortable: true, minWidth: '110px' },
        { name: 'İcra Dosya No', selector: row => row.enforcementCaseNumber || '-', sortable: true, minWidth: '130px' },
        {
            name: 'Tahsilat Tutarı',
            selector: row => {
                const pc = row.paymentCollections;
                return formatCurrency(pc?.amount || row.collectedAmount);
            },
            sortable: true,
            minWidth: '130px'
        },
        {
            name: 'Ödeme Tarihi',
            selector: row => formatDate(row.paymentDate || row.paymentCollections?.date),
            sortable: true,
            minWidth: '110px'
        },
        {
            name: 'Ata / Kaldır',
            cell: (row) => {
                const appointedUser = row.appointedUser || row.appointedUsers;
                const hasAppointedUser = appointedUser && (
                    Array.isArray(appointedUser)
                        ? appointedUser.filter(u => !!u && String(u).trim() !== '').length > 0
                        : !!appointedUser
                );

                if (!hasAppointedUser) {
                    return (
                        <Btn attrBtn={{ color: 'outline-primary', size: 'sm', onClick: () => handleAssignToSelf(row.id), title: 'Kendine Ata', disabled: isAssigning }}>
                            <UserPlus size={16} />
                        </Btn>
                    );
                }

                return (
                    <Btn attrBtn={{ color: 'outline-danger', size: 'sm', onClick: () => handleReleaseFromSelf(row.id), title: 'Kendinden Kaldır', disabled: isReleasing }}>
                        <UserX size={16} />
                    </Btn>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '100px',
            center: true
        },
        {
            name: 'Detay',
            cell: (row) => (
                <div
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => navigate(`${process.env.PUBLIC_URL}/compensation-detail/${row.id}`)}
                    title="Kapak Detayı"
                >
                    <Edit size={16} />
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '70px',
            center: true
        },
    ];

    return (
        <>
            <Row className="mt-4">
                <Col sm='12'>
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                <Media body className='text-end mt-3'>
                                    {shouldShowFileCloseButton() && (
                                        <div
                                            className="btn btn-outline-secondary ms-2"
                                            onClick={handleFileClose}
                                            style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}
                                        >
                                            <X size={18} className="me-1" />
                                            Dosya Kapamaya Gönder
                                        </div>
                                    )}
                                </Media>
                            </div>

                            <div style={{ height: "40px" }}></div>

                            <div className='table-responsive'>
                                <DataTable
                                    columns={columns}
                                    data={data}
                                    onSelectedRowsChange={handleRowSelected}
                                    pagination
                                    paginationServer
                                    clearSelectedRows={clearSelectedRows}
                                    paginationTotalRows={totalRows}
                                    onChangePage={page => setPage(page)}
                                    onChangeRowsPerPage={pageSize => { setPage(1); setPageSize(pageSize); }}
                                    striped
                                    noDataComponent={<div className="p-4 text-center">Kapak bulunamadı.</div>}
                                    progressPending={!data}
                                    progressComponent={<p>Yükleniyor...</p>}
                                />
                            </div>

                            {Array.isArray(data) && data.length > 0 && (
                                <div className="mt-4">
                                    <h6 className="mb-3">Dosyalar</h6>
                                    {data.map((row, idx) => (
                                        <FilesCollapsible
                                            key={row.id}
                                            title={row.enforcementCaseNumber ? `İcra Dosya No: ${row.enforcementCaseNumber}` : `Kapak #${idx + 1} Dosyaları`}
                                            referenceId={row.id}
                                        />
                                    ))}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Dosya Kapatma Modalı */}
            <FileCloseModal
                isOpen={isFileCloseModalOpen}
                toggle={() => setIsFileCloseModalOpen(false)}
                arbitrationId={arbitrationId}
                refreshArbitrationData={refreshArbitrationData}
            />
        </>
    );
};

export default CompensationInformations;
